import { useUnitConversion } from '@/composables/useUnitConversion';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

/**
 * Composable for generating PDF reports of projectors and LED tiles
 */
export const usePdfExport = () => {
  const cruxModuleStore = useCruxModuleStore();

  const { formatLength, convertLength, getLengthUnitLabel } = useUnitConversion();

  /**
   * Generate and download a PDF report of projectors and LED screens
   */
  const generateProjectReport = () => {
    // Create PDF document
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Get project name
    const projectName = 'Untitled Project';
    const currentDate = new Date().toLocaleString();

    // Add title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('Project Report', pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    doc.text(`Project: ${projectName}`, 15, 25);
    doc.text(`Generated: ${currentDate}`, 15, 32);

    // Get all projectors
    const projectors = cruxModuleStore.filterModulesByType('projector');

    // Get all LED screens
    const ledScreens = cruxModuleStore.filterModulesByType('led_screen');

    const unitLabel = getLengthUnitLabel();

    let currentY = 40;

    // Add Projectors section
    if (projectors.length > 0) {
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Projectors', 15, currentY);
      currentY += 5;

      // Prepare projector data
      const projectorData = projectors.map((proj) => {
        const position = proj.data?.position || { x: 0, y: 0, z: 0 };
        const rotation = proj.data?.rotation || { x: 0, y: 0, z: 0 };
        const target = proj.data?.target || { x: 0, y: 0, z: 0 };

        return [
          `${proj.data?.brand?.name || ''} ${proj.data?.name || 'Unnamed'}`,
          `${formatLength(position.x)}, ${formatLength(position.y)}, ${formatLength(position.z)}`,
          `${((rotation.x * 180) / Math.PI).toFixed(1)}°, ${((rotation.y * 180) / Math.PI).toFixed(1)}°, ${((rotation.z * 180) / Math.PI).toFixed(1)}°`,
          `${formatLength(target.x)}, ${formatLength(target.y)}, ${formatLength(target.z)}`,
          proj.data?.lumens || 'N/A',
          proj.data?.resolution || 'N/A',
          proj.data?.throwRatio?.toFixed(2) || 'N/A',
        ];
      });

      // Add projector table
      autoTable(doc, {
        startY: currentY,
        head: [
          [
            'Name',
            `Position (${unitLabel})`,
            'Rotation (°)',
            `Target (${unitLabel})`,
            'Lumens',
            'Resolution',
            'Throw Ratio',
          ],
        ],
        body: projectorData,
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [147, 51, 234], fontStyle: 'bold' },
        margin: { left: 15, right: 15 },
        tableWidth: 'auto',
        didDrawPage: (data) => {
          // Add page numbers
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
            pageWidth - 20,
            pageHeight - 10,
            { align: 'right' }
          );
        },
      });

      currentY = doc.lastAutoTable.finalY + 10;
    } else {
      doc.setFontSize(10);
      doc.setFont(undefined, 'italic');
      doc.text('No projectors found in this project.', 15, currentY);
      currentY += 10;
    }

    // Add LED Screens section
    if (ledScreens.length > 0) {
      // Check if we need a new page
      if (currentY > pageHeight - 60) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('LED Screens', 15, currentY);
      currentY += 5;

      // Prepare LED screen data
      const ledData = ledScreens.map((led) => {
        const position = led.data?.position || { x: 0, y: 0, z: 0 };
        const rotation = led.data?.rotation || { x: 0, y: 0, z: 0 };
        const scale = led.data?.scale || { x: 1, y: 1, z: 1 };
        const horizontalCurve = led.data?.horizontalCurvature ? led.data?.horizontalCurvature : 'N/A';
        const verticalCurve = led.data?.verticalCurvature ? led.data?.verticalCurvature : 'N/A';

        return [
          `${led.data?.brand?.name || ''} ${led.data?.name || 'Unnamed'}`,
          `${formatLength(position.x)}, ${formatLength(position.y)}, ${formatLength(position.z)}`,
          `${rotation.x.toFixed(1)}°, ${rotation.y.toFixed(1)}°, ${rotation.z.toFixed(1)}°`,
          `${led.data?.horizontalSize * led.data?.verticalSize} (${led.data?.horizontalSize || 0} x ${led.data?.verticalSize || 0})`,
          led.data?.brightness_nits || 'N/A',
          `${horizontalCurve}°`,
          `${verticalCurve}°`,
        ];
      });

      // Add LED screen table
      autoTable(doc, {
        startY: currentY,
        head: [
          [
            'Name',
            `Position (${unitLabel})`,
            'Rotation (°)',
            'Tile Count (H×V)',
            'Brightness (nits)',
            `H. Curvature`,
            `V. Curvature`,
          ],
        ],
        body: ledData,
        theme: 'striped',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [147, 51, 234], fontStyle: 'bold' },
        margin: { left: 15, right: 15 },
        tableWidth: 'auto',
        didDrawPage: (data) => {
          // Add page numbers
          const pageCount = doc.internal.getNumberOfPages();
          doc.setFontSize(8);
          doc.text(
            `Page ${doc.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
            pageWidth - 20,
            pageHeight - 10,
            { align: 'right' }
          );
        },
      });

      currentY = doc.lastAutoTable.finalY + 10;
    } else {
      // Check if we need a new page
      if (currentY > pageHeight - 20) {
        doc.addPage();
        currentY = 20;
      }

      doc.setFontSize(10);
      doc.setFont(undefined, 'italic');
      doc.text('No LED screens found in this project.', 15, currentY);
    }

    // Generate filename with project name and timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `${projectName.replace(/[^a-z0-9]/gi, '_')}_Report_${timestamp}.pdf`;

    // Open PDF in new tab for preview (comment out to save to disk instead)
    window.open(doc.output('bloburl'), '_blank');

    // Save the PDF to disk (comment out to only preview)
    // doc.save(filename)
  };

  return {
    generateProjectReport,
  };
};
